// El emoji de cada FAMILIA de cursos, y a qué familia pertenece un curso.
//
// POR QUÉ POR FAMILIA Y NO POR CURSO. Lo decidió Gabri el 6 de octubre de 2026: «los de
// sociolingüística todos llevan el mismo emoji y se agrupan en una misma etiqueta desplegable,
// de modo que el alumno escoge el que quiere, indicando curso al lado». El emoji no identifica a
// Sociolingüística III: identifica a Sociolingüística, y dentro el alumno elige nivel.
//
// Y EL NIVEL ORIENTA, NO CIERRA. «El curso debe ser orientativo para el alumno, no obligatorio,
// y eso importa» —sus palabras—. Quien decide a qué lección LLEGA es la `etapa` de la lección
// (`backend/app/domain/acceso.py`); esto es una etiqueta para encontrar las cosas, y no una
// puerta. Si alguna vez un emoji empieza a decidir algo, está mal usado.
//
// Los dieciocho los eligió él de cuatro candidatos por familia, y ninguno se repite: el emoji
// es lo que distingue una etiqueta de otra de un vistazo, así que dos iguales la romperían.
// Lo vigila `scripts/prueba_emojis_familia.mjs`.

import { BLOQUE_CATALANA, FAMILIAS_CATALANAS } from "./familiasCatalanas.mjs";

// LOS BLOQUES DE COMUNICACIÓN, del 19 de octubre de 2026. Gabri, al ver las dos literaturas
// nuevas dentro de las tarjetas de Inglés y de Alemán: *«deberíamos crear una tarjeta de Lengua
// y Literatura Castellana, y dentro Educación literaria, Comunicación, Sociolingüística,
// Ortografía… porque se da por sentado que son en español»*. Una tarjeta más arriba habría
// convertido las familias en líneas de un desplegable, y sus niveles —Ortografía I, II…— en un
// tercer piso que no se ve. Así que el nivel nuevo es un RÓTULO encima de las tarjetas, que no
// se pulsa: ordena sin añadir un clic. Los nombres son suyos, y son las tres materias del BOE.
//
// EL ORDEN DE LA LISTA ES EL DE LA PANTALLA. Una familia sin bloque no lleva rótulo, que es lo
// que pasa en las otras cuatro competencias: ahí todo sigue como estaba.
const LENGUA = "Lengua y literatura castellana";
const LITERATURA = "Literatura universal";
const EXTRANJERAS = "Lenguas extranjeras";
// El catalán va justo detrás del castellano, con su propio rótulo (C9, 3 nov 2026).
export const BLOQUES = [LENGUA, BLOQUE_CATALANA, LITERATURA, EXTRANJERAS];
/** El bloque que en «Qué te interesa» es UNA casilla: lo que se estudia en español (`lib/cursosDelTest.mjs`). */
export const BLOQUE_LENGUA = LENGUA;
/** Y su pareja en catalán: también una sola casilla, por muchas familias que tenga. */
export { BLOQUE_CATALANA };

export const FAMILIAS = [
  { familia: "Sociolingüística", bloque: LENGUA, emoji: "🌐" },
  { familia: "Gramática", bloque: LENGUA, emoji: "🧩" },
  // «ORTOGRAFÍA» A SECAS desde el 8 de octubre de 2026. Gabri: *«el título lo dejamos en
  // Ortografía, porque acentuación ya está implícito en ortografía desde mi punto de vista»*.
  // El nombre largo se queda de alias: los títulos del CMS que lo usen siguen cayendo aquí.
  { familia: "Ortografía", bloque: LENGUA, emoji: "🔤", tambien: ["Ortografía y acentuación"] },
  // LÉXICO Y SEMÁNTICA (28 oct 2026) nació sin familia, y cada nivel salía en su tarjeta suelta,
  // seis seguidas en el catálogo. Lo vio Gabri el 30 de octubre: *«deben estar en una sola
  // tarjeta, e indicar I, II, III»*. La lupa la eligió él ese mismo día.
  { familia: "Léxico y Semántica", bloque: LENGUA, emoji: "🔎" },
  { familia: "Educación literaria", bloque: LENGUA, emoji: "🎭" },
  // LITERATURA UNIVERSAL (8 oct 2026) es familia propia y no un nivel de Educación literaria:
  // es otra asignatura del catálogo —`literatura-universal`, 1.º de Bachillerato, modalidad de
  // letras— con la misma materia. Y el emoji NO es un globo: 🌍 al lado del 🌐 de
  // Sociolingüística son dos globos, y dos globos en un desplegable no se distinguen de un
  // vistazo, que es lo único que esta lista tiene que conseguir. El portero no lo habría
  // cazado: sólo mira que no se repitan **exactos**.
  { familia: "Literatura Universal", bloque: LITERATURA, emoji: "📚" },
  // LAS DOS LITERATURAS EN ESPAÑOL, del 19 de octubre de 2026. Sin familia propia caían en
  // Alemán y en Inglés, porque «Literatura alemana» lleva «aleman» dentro y gana el nombre
  // más largo que encaje: con el suyo puesto, el más largo es el suyo. Los emojis los eligió
  // Gabri, y no son banderas a propósito: la bandera dice «se estudia la lengua».
  { familia: "Literatura alemana", bloque: LITERATURA, emoji: "🏰" },
  { familia: "Literatura inglesa y americana", bloque: LITERATURA, emoji: "🎩" },
  { familia: "Comunicación", bloque: LENGUA, emoji: "🗣️" },
  ...FAMILIAS_CATALANAS,
  { familia: "Inglés", bloque: EXTRANJERAS, emoji: "🇬🇧" },
  { familia: "Alemán", bloque: EXTRANJERAS, emoji: "🇩🇪" },
  { familia: "Historia de la Filosofía", emoji: "⏳" },
  { familia: "Filosofía", emoji: "❓" },
  { familia: "Valores cívicos y éticos", emoji: "🕊️", tambien: ["Valores cívicos", "Valores Cívicos"] },
  { familia: "Neuropsicología aplicada", emoji: "🧿", tambien: ["Neuropsicología"] },
  { familia: "Psicología aplicada", emoji: "📋" },
  { familia: "Psicología", emoji: "🫀" },
  { familia: "Orientación personal y profesional", emoji: "🎒", tambien: ["Orientación"] },
  { familia: "Música", emoji: "🎵" },
  { familia: "Latín", emoji: "🏺" },
  { familia: "Cultura Clásica", emoji: "🏛️" },
  // POLÍTICA SE QUEDÓ SIN EMOJI hasta el 17 de octubre de 2026, y no dio ningún error: un
  // curso que no cae en ninguna familia sale igual —es la regla de la casa, lo que no se
  // reconoce se enseña— pero sale con el hueco del emoji en blanco, y en una rejilla de
  // tarjetas eso se lee como una tarjeta a medio hacer. Lo vio Gabri en el catálogo y eligió
  // el apretón de manos. El título del CMS es «Política: ideologías y España», así que va de
  // alias: la familia se llama por lo corto, que es lo que cabe en la etiqueta.
  { familia: "Política", emoji: "🤝", tambien: ["Política: ideologías y España"] },
];

/** Sin tildes, en minúsculas. Los títulos del CMS los escribe una persona. */
function llano(t) {
  return String(t ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

// GANA EL NOMBRE MÁS LARGO QUE ENCAJE, y no es un detalle de implementación: «Historia de la
// Filosofía» contiene «Filosofía», «Psicología aplicada» contiene «Psicología» y
// «Neuropsicología» contiene «psicología» dentro de una palabra. Con el primero que case,
// Historia de la Filosofía acabaría en la etiqueta de Filosofía y nadie lo vería hasta abrir
// el desplegable. Con el más largo, cada una cae donde debe.
const INDICE = FAMILIAS
  .flatMap((f) => [f.familia, ...(f.tambien || [])].map((nombre) => ({ clave: llano(nombre), f })))
  .sort((a, b) => b.clave.length - a.clave.length);

/** La familia de un curso por su título, o `null` si no se reconoce. */
export function familiaDe(titulo) {
  const t = llano(titulo);
  if (!t) return null;
  const hallado = INDICE.find((e) => t.includes(e.clave));
  return hallado ? hallado.f : null;
}

/** El emoji de un curso por su título. Sin familia no hay emoji, y eso se dice con `null`. */
export function emojiDe(titulo) {
  const f = familiaDe(titulo);
  return f ? f.emoji : null;
}

export default { FAMILIAS, familiaDe, emojiDe };
